import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import FilterButton from '../../SharedComponents/FilterButton';
import FilterDropdown from '../../SharedComponents/FilterDropdown';
import MobileModal from '../../SharedComponents/MobileModal';
import styles from '../../searchV2.scss';
import useIsMobile from '../../../../../components/hooks/useIsMobile';
import {
    createAllEventSeriesOptions,
    isAllEventSeriesOptionSelected,
    trackAllEventSeriesFilters,
} from '../utils/filterHelpers';

const AllEventSeriesFilter = (props) => {
    const {
        filters,
        tourOptions,
        handleFilterUpdate,
        onMobileClick,
        hasResetFilters,
    } = props;

    const { isMobileWidth } = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [localFilterInputs, setLocalFilterInputs] = useState({});
    const wrapperRef = useRef(null);
    const allOptions = createAllEventSeriesOptions(tourOptions);

    // Check if any regular tours or flex league is active
    const isToursActive = filters?.tours && Object.values(filters.tours).some(value => value);
    const isFlexLeagueActive = filters?.eventTypes?.flex_league;
    const isActive = isToursActive || isFlexLeagueActive;

    // Initialize local filter inputs when modal opens or filters change normally
    useEffect(() => {
        if (isOpen) {
            const combinedInputs = {
                ...filters?.tours || {},
                flex_league: filters?.eventTypes?.flex_league || false,
            };
            setLocalFilterInputs(combinedInputs);
        }
    }, [filters?.tours, filters?.eventTypes?.flex_league, isOpen]);

    // Handle reset trigger - clear local state when reset is triggered
    useEffect(() => {
        if (hasResetFilters) {
            setLocalFilterInputs({
                flex_league: false,
            });
        }
    }, [hasResetFilters]);

    // Handle filter interactions
    const handleFilterClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(!isOpen);
    };

    const closeModal = () => {
        setIsOpen(false);
        setLocalFilterInputs({});
    };

    const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            closeModal();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
            closeModal();
        }
    };

    useEffect(() => {
        if (isOpen && !isMobileWidth) {
            const mouseDown = 'mousedown';
            const touchEnd = 'touchend';
            const keyDown = 'keydown';

            document.addEventListener(mouseDown, handleClickOutside);
            document.addEventListener(touchEnd, handleClickOutside, false);
            document.addEventListener(keyDown, handleKeyDown);
            return () => {
                document.removeEventListener(mouseDown, handleClickOutside);
                document.removeEventListener(touchEnd, handleClickOutside, false);
                document.removeEventListener(keyDown, handleKeyDown);
            };
        }
    }, [isOpen, isMobileWidth]);

    const handleOptionChange = (optionValue, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const currentValues = localFilterInputs || {};
        const updatedInputs = {
            ...currentValues,
            [optionValue]: !currentValues[optionValue],
        };
        setLocalFilterInputs(updatedInputs);
    };

    const handleApply = () => {
        const { flex_league: flexLeague, ...tourFilters } = localFilterInputs;

        // Build the complete filter update
        const updatedEventTypes = { ...filters?.eventTypes };
        if (flexLeague) {
            updatedEventTypes.flex_league = true;
        } else {
            delete updatedEventTypes.flex_league;
        }

        const completeFilterUpdate = {
            tours: tourFilters,
            eventTypes: updatedEventTypes,
        };

        // Use the atomic update approach to avoid state timing issues
        handleFilterUpdate(completeFilterUpdate, 'submit');

        // Track using shared utility
        trackAllEventSeriesFilters(filters, localFilterInputs);

        setIsOpen(false);
    };

    const handleCancel = () => {
        closeModal();
    };

    const getButtonClasses = () => {
        const activeClass = isActive ? styles.searchFilterBtnActive : '';
        return `${styles.tagHover} ${styles.searchFilterBtn} ${activeClass} text-capitalize`;
    };

    const renderContent = () => (
        <div>
            {allOptions.length === 0 ? (
                <div className={styles.noOptionsMessage} data-testid="search.events.filters.all-event-series.no-options-message">
                    No options available
                </div>
            ) : (
                allOptions.map((option) => {
                    const isSelected = isAllEventSeriesOptionSelected(option.value, filters, localFilterInputs);
                    return (
                        <div
                            key={option.value}
                            className={`${styles.optionItem} ${isSelected ? 'selected' : ''}`}
                        >
                            <div className={styles.optionContent}>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleOptionChange(option.value)}
                                    className={styles.optionCheckbox}
                                    data-testid={`search.events.filters.all-event-series.option-${option.value}.checkbox`}
                                />
                                <span
                                    className={styles.optionLabel}
                                    onClick={() => handleOptionChange(option.value)}
                                >
                                    {option.label}
                                </span>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    const renderDesktopDropdown = () => (
        <FilterDropdown
            onCloseClick={handleCancel}
            handleFilterSubmit={handleApply}
            showHeader={false}
            data-testid="search.events.filters.all-event-series.dropdown"
        >
            {renderContent()}
        </FilterDropdown>
    );

    // On mobile, render just a button that triggers the parent's modal
    if (isMobileWidth && onMobileClick) {
        return (
            <FilterButton
                isActive={isActive}
                onClick={onMobileClick}
            >
                All Event Series
            </FilterButton>
        );
    }

    // On desktop, use custom filter implementation
    return (
        <div className={styles.filterWrapper} ref={wrapperRef}>
            <button
                type="button"
                className={getButtonClasses()}
                onClick={handleFilterClick}
                data-testid="search.events.filters.all-event-series.trigger-btn"
            >
                All Event Series
            </button>
            {isOpen && !isMobileWidth && renderDesktopDropdown()}
            <MobileModal
                isOpen={isOpen && isMobileWidth}
                onClose={closeModal}
                title="All Event Series"
                onApply={handleApply}
            >
                {renderContent()}
            </MobileModal>
        </div>
    );
};

AllEventSeriesFilter.propTypes = {
    filters: PropTypes.instanceOf(Object).isRequired,
    tourOptions: PropTypes.instanceOf(Array).isRequired,
    handleFilterUpdate: PropTypes.func.isRequired,
    onMobileClick: PropTypes.func,
    hasResetFilters: PropTypes.bool,
};

AllEventSeriesFilter.defaultProps = {
    onMobileClick: null,
    hasResetFilters: false,
};

export default AllEventSeriesFilter;
